<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>

  @if (unit) {
    <app-page-detail-hero [auditStamp]="unit"
      icon="pi pi-warehouse"
      [eyebrow]="'Stock Unit #' + unit.id"
      [title]="unit.unitSku || unit.unitCode || itemIdentityLabel(unit, 'Stock Unit')"
      [subtitle]="masterName('WAREHOUSE', unit.warehouseId) + ' | ' + masterName('ZONE', unit.zoneId)">
      <div tags>
        <p-tag [value]="unit.status" [severity]="statusSeverity(unit.status)"></p-tag>
        <p-tag [value]="masterName('ITEM_CONDITION', unit.conditionId)" severity="secondary"></p-tag>
      </div>
      <div actions>
        <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/warehousing/stock"></p-button>
        <p-button label="View Transactions" icon="pi pi-history" severity="secondary" [outlined]="true"
          [routerLink]="['/warehousing/stock-ledger']" [queryParams]="{ stockUnitId: unit.id }"></p-button>
        <p-button *ngIf="unit.containerCaseId" label="Open Case" icon="pi pi-box" severity="secondary" [outlined]="true"
          [routerLink]="['/container-tracking/cases/view', unit.containerCaseId]"></p-button>
        <p-button *ngIf="canReturn()" label="Return to Stock" icon="pi pi-replay" severity="success" (click)="openReturnDialog()"></p-button>
        <p-button label="Mark Refurbishment" icon="pi pi-wrench" severity="warn" routerLink="/warehousing/refurbishment/add"></p-button>
      </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <div class="warehousing-detail-grid">
      <app-page-form-section icon="pi pi-box" title="Stock Identity" subtitle="Item and stock unit identifiers.">
        <div class="warehousing-detail-list">
          <div><span>Item</span><strong>{{ unit.itemName || '-' }}</strong></div>
          <div><span>Item Number</span><strong>{{ unit.itemNumber || '-' }}</strong></div>
          <div *ngIf="itemSizeLabel(unit)"><span>Size</span><strong>{{ itemSizeLabel(unit) }}</strong></div>
          <div><span>Unique Unit SKU</span><strong>{{ unit.unitSku || unit.unitCode || '-' }}</strong></div>
          <div><span>Client ID</span><strong>{{ unit.crmClientId || '-' }}</strong></div>
          <div><span>Account ID</span><strong>{{ unit.crmAccountId || '-' }}</strong></div>
          <div><span>Container Case</span><strong>{{ unit.containerCaseId ? ('Case #' + unit.containerCaseId) : '-' }}</strong></div>
        </div>
      </app-page-form-section>

      <app-page-form-section icon="pi pi-map-marker" title="Storage Location" subtitle="Current warehouse position and condition.">
        <div class="warehousing-detail-list">
          <div><span>Warehouse</span><strong>{{ masterName('WAREHOUSE', unit.warehouseId) }}</strong></div>
          <div><span>Zone</span><strong>{{ masterName('ZONE', unit.zoneId) }}</strong></div>
          <div><span>Condition</span><strong>{{ masterName('ITEM_CONDITION', unit.conditionId) }}</strong></div>
          <div><span>Received At</span><strong>{{ unit.receivedAt || '-' }}</strong></div>
          <div><span>Dispatched At</span><strong>{{ unit.dispatchedAt || '-' }}</strong></div>
          <div><span>Refurbishment Source</span><strong>{{ unit.refurbishmentSource || '-' }}</strong></div>
        </div>
      </app-page-form-section>
    </div>

    <app-page-form-section icon="pi pi-history" title="Movement History"
      subtitle="Recent item transaction activity for this stock unit.">
      <div actions>
        <p-button label="Open Item Transactions" icon="pi pi-arrow-right" severity="secondary" [outlined]="true"
          [routerLink]="['/warehousing/stock-ledger']" [queryParams]="{ stockUnitId: unit.id }"></p-button>
      </div>

      <p-table [value]="movements" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table"
        [tableStyle]="{ 'min-width': '72rem' }">
        <ng-template pTemplate="header">
          <tr>
            <th>Movement</th>
            <th>Date</th>
            <th>Reference</th>
            <th>Route</th>
            <th class="text-right">Quantity</th>
            <th>Status Flow</th>
            <th>Notes</th>
            <th class="text-center">Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ movementLabel(row) }}</td>
            <td>{{ row.movementDate || '-' }}</td>
            <td>{{ row.referenceNumber || '-' }}</td>
            <td>{{ movementRoute(row) }}</td>
            <td class="text-right">
              <p-tag [value]="quantityLabel(row)" [severity]="quantityTone(row)"></p-tag>
            </td>
            <td>{{ statusFlow(row) }}</td>
            <td>{{ row.notes || '-' }}</td>
            <td class="text-center">
              <div class="warehousing-row-actions">
                <p-button *ngIf="row.containerCaseId" icon="pi pi-box" [text]="true"
                  [routerLink]="['/container-tracking/cases/view', row.containerCaseId]"></p-button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="8">
              <div class="warehousing-empty-state">
                <i class="pi pi-history"></i>
                <strong>No stock movements found</strong>
                <span>No ledger rows have been recorded for this stock unit yet.</span>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </app-page-form-section>
  } @else if (!isLoading) {
    <div class="warehousing-empty-detail">
      <i class="pi pi-search"></i>
      <strong>Stock unit not found</strong>
      <span>The selected stock unit could not be loaded.</span>
    </div>
  }

  <p-dialog header="Return unit to stock" [(visible)]="returnDialogVisible" [modal]="true" [style]="{ width: 'min(46rem, 94vw)' }">
    <form [formGroup]="returnForm" class="formgrid grid p-fluid">
      <div class="field col-12"><p class="m-0 text-600">Confirm the destination for <strong>{{ unit?.unitSku || unit?.unitCode }}</strong>. This records a unit return movement.</p></div>
      <div class="field col-12 md:col-6"><label for="returnRegion">Region</label><p-select inputId="returnRegion" [options]="masterData.REGION" optionLabel="name" optionValue="id" formControlName="regionId" [filter]="true" styleClass="w-full"></p-select></div>
      <div class="field col-12 md:col-6"><label for="returnWarehouse">Own warehouse</label><p-select inputId="returnWarehouse" [options]="masterData.WAREHOUSE" optionLabel="name" optionValue="id" formControlName="warehouseId" [filter]="true" styleClass="w-full"></p-select></div>
      <div class="field col-12 md:col-6"><label for="returnZone">Zone</label><p-select inputId="returnZone" [options]="masterData.ZONE" optionLabel="name" optionValue="id" formControlName="zoneId" [filter]="true" styleClass="w-full"></p-select></div>
      <div class="field col-12 md:col-6"><label for="returnCondition">Condition</label><p-select inputId="returnCondition" [options]="masterData.ITEM_CONDITION" optionLabel="name" optionValue="id" formControlName="conditionId" [filter]="true" styleClass="w-full"></p-select></div>
      <div class="field col-12"><label for="returnNotes">Notes</label><textarea id="returnNotes" pTextarea rows="3" formControlName="notes" class="w-full"></textarea></div>
      <div class="col-12 flex justify-content-end gap-2"><p-button label="Cancel" severity="secondary" [outlined]="true" (click)="returnDialogVisible = false"></p-button><p-button label="Return to Stock" icon="pi pi-replay" [loading]="isReturning" (click)="submitReturn()"></p-button></div>
    </form>
  </p-dialog>
</app-page-shell>
